import { FC, useCallback, useEffect, useState } from "react";
import styles from './styles/MarketingBonusCalculator.module.scss';
import GridWrapper from "../../../components/controls/GridWrapper";
import { PluginWrapper } from "../../../components/plugins";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { t } from "i18next";
import { Button } from "../../../components/buttons/button";
import { MarketingBonusCalculatorDataProvider } from "../../../Services/DataProviders/MarketingBonusCalculatorDataProvider";
import { IErrorResponseMarketingBonusCalculationCompanyStatus, IMarketingBonusCalculationAllCompaniesDTO, IMarketingBonusCalculationAllCompaniesStatusDTO, IMarketingBonusCalculationOneCompanyDTO, IMarketingBonusCalculationOneCompanyStatusDTO } from "../../../libs/coreapi-dto/dirs/marketingBonusCalculator";
import { ICompanyDTO } from "../../../libs/coreapi-dto/service/company";
import { CompanyDataProvider } from "../../../Services/DataProviders/CompanyDataProvider";
import renderGlobalAlert from "../../../system/hooks/useGlobalAlert";
import { useTokenData } from "../../../system/hooks/useTokenData";
import { Select } from "antd";
import BaseCommandsPanel from "../../../components/commandsPanels/BaseCommandsPanel";
import Tooltip from "../../../components/tooltips/Tooltip";
import { RotationRightButton } from "../../../components/buttons/iconButtons/action/RotationCollection";
import { DateTime } from "luxon";
import { DeleteButton } from "../../../components/buttons/iconButtons/action/MainCollection";
import { Spinner } from "../../../components/spiner/Spinner";

interface ICompanyItem {
    label: string;
    value: string;
    content?: ICompanyDTO;
}

interface IStatusMessage {
    text: string;
    type: 'success' | 'error' | 'info' | 'warning';
}

export const MarketingBonusCalculatorView: FC = () => {
    const appCtx = useAppContext();
    const companyDataProvider = new CompanyDataProvider(appCtx.coreApiService);
    const marketingBonusCalculatorDP = new MarketingBonusCalculatorDataProvider(appCtx.coreApiService);
    const { getCompanyId } = useTokenData();

    const [companies, setCompanies] = useState<ICompanyItem[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [forceRestart, setForceRestart] = useState<boolean>(false);
    const [executionId, setExecutionId] = useState<string | null>(null);
    const [jobId, setJobId] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<IStatusMessage | null>(null);

    const baseT = (value: string) => t('directory.marketingBonusCalculator.' + value);

    const loadCompanies = useCallback(async () => {
        setIsLoading(true);

        try {
            const companyItems = await new Promise<ICompanyItem[]>((resolve) => {
                companyDataProvider.viewCompany(
                    { pageNumber: 1, numberPerPage: 1000, columnFilters: [] },
                    (items: ICompanyDTO[]) => {
                        const itemsList: ICompanyItem[] = items.map(x => ({
                            value: x.id,
                            label: x.name,
                            content: x,
                        }));
                        resolve(itemsList);
                    }
                );
            });

            const activeCompanyItems = companyItems.filter(item => item.content?.isActiveCompany === true);

            setCompanies(activeCompanyItems);
        } catch (error) {
            renderGlobalAlert({
                variant: "error",
                statusCode: 500,
                title: baseT("errorCompaniesViewRequest"),
            });
        } finally {
            setIsLoading(false);
        }
    }, [companyDataProvider, getCompanyId]);

    const handleCompanyChange = useCallback(async (companyId: string | null) => {
        if (!companyId || companyId === selectedCompanyId) return;

        setSelectedCompanyId(companyId);
        setStatusMessage(null);
        setExecutionId(null)
        setJobId(null)

    }, [selectedCompanyId]);

    const formatDateTime = (dateTime: string | DateTime | null | undefined): string => {
        if (!dateTime) {
            return '—';
        }

        const dt = typeof dateTime === 'string'
            ? DateTime.fromISO(dateTime)
            : dateTime;

        if (!dt.isValid) {
            return '—';
        }

        return dt.toFormat('dd.MM.yyyy HH:mm');
    };

    const getCompanyName = useCallback(() => {
        if (!selectedCompanyId) return 'всех компаний ЦО';
        const company = companies.find(c => c.value === selectedCompanyId);
        return company?.label || selectedCompanyId;
    }, [selectedCompanyId, companies]);

    const updateStatusMessage = useCallback((
        status: string,
        companyName: string,
        completedAt?: DateTime | string | null,
        errorMessage?: string,
        oneCompany?: boolean
    ) => {
        const formattedDate = completedAt ? formatDateTime(completedAt) : '';

        let text = '';
        let type: 'success' | 'error' | 'info' | 'warning' = 'info';
        const isOneCompany = oneCompany ?? false;

        const companyPart = isOneCompany
            ? `по компании ${companyName}`
            : `по всем компаниям ЦО`;

        switch (status) {
            case 'Completed':
                text = `Пересчёт бонусов ${companyPart} успешно завершён ${formattedDate}`;
                type = 'success';
                break;
            case 'Failed':
                text = `Пересчёт бонусов ${companyPart} не был произведён. ${errorMessage || 'Попробуйте позднее'}`;
                type = 'error';
                break;
            case 'Running':
                text = `Пересчёт бонусов ${companyPart} в процессе работы`;
                type = 'info';
                break;
            case 'Custom':
                text = errorMessage || 'Информационное сообщение';
                type = 'info';
                break;
            default:
                text = `Статус пересчёта ${companyPart}: ${status}`;
                type = 'warning';
        }

        setStatusMessage({ text, type });
    }, []);

    //ПЕРЕСЧЕТ БОНУСОВ
    const handleCalculate = useCallback(() => {
        setStatusMessage(null);
        setIsCalculating(true);
        setIsLoading(true);

        const handleError = (err: any) => {

            let errorMessage = 'Неизвестная ошибка';
            if (typeof err === 'string') {
                errorMessage = err;
            } else if (err?.message) {
                errorMessage = err.message;
            } else if (err?.detail) {
                errorMessage = err.detail;
            }

            updateStatusMessage('Failed', companyName, undefined, errorMessage);

            setIsCalculating(false);
            setIsLoading(false);
        };

        const companyName = getCompanyName();
        updateStatusMessage('Running', companyName);

        if (!selectedCompanyId) {
            setSelectedCompanyId(null)
            marketingBonusCalculatorDP.calculateAll(
                (data: IMarketingBonusCalculationAllCompaniesDTO) => {
                    const newExecutionId = data.executionId;
                    const newJobId = data.hangfireJobId;
                    const error = data.message;

                    if (executionId || newJobId) {
                        setExecutionId(newExecutionId);
                        setJobId(newJobId);
                        if (error) {
                            updateStatusMessage('Custom', companyName, undefined, error);
                        }
                    }

                    setIsLoading(false);
                    setIsCalculating(false);
                },
                null,
                undefined,
                forceRestart
            )
            return;
        }

        marketingBonusCalculatorDP.calculateOne(
            selectedCompanyId,
            (data: IMarketingBonusCalculationOneCompanyDTO) => {
                if (data) {
                    handleRefreshStatus();
                    setIsLoading(false);
                    setIsCalculating(false);
                }
            },
            null,
            handleError,
            forceRestart
        )
    }, [selectedCompanyId, getCompanyName, forceRestart]);

    //  СТАТУС ПЕРЕСЧЕТА
    const handleRefreshStatus = useCallback(() => {
        const companyName = getCompanyName();
        setIsLoading(true);
        setIsCalculating(true);
        const isOneCompany = selectedCompanyId !== null

        //Функция для ответа от getStatusOne (одна компания)
        const handleOneCompanyResponse = (data: IMarketingBonusCalculationOneCompanyStatusDTO) => {

            const execution = data.currentExecution;
            const status = execution?.status || 'Completed';
            const completedAt = execution?.completedAt || undefined;
            const errorMessage = execution?.errorMessage || undefined;

            updateStatusMessage(status, companyName, completedAt, errorMessage, true);
            setIsCalculating(false);
            setIsLoading(false);
        };

        //Функция для ответа от getStatusAll/getStatusAllByJobId (все компании)
        const handleAllCompaniesResponse = (data: IMarketingBonusCalculationAllCompaniesStatusDTO) => {

            const status = data.status || 'Completed';
            const completedAt = data.completedAt || undefined;
            const errorMessage = data.errorMessage || undefined;

            updateStatusMessage(status, companyName, completedAt, errorMessage, false);
            setIsCalculating(false);
            setIsLoading(false);
        };

        //Общая функция обработки ошибки
        const handleError = (err: IErrorResponseMarketingBonusCalculationCompanyStatus) => {
            updateStatusMessage('Failed', companyName, undefined, err.detail, isOneCompany);
            setIsCalculating(false);
            setIsLoading(false);
        };

        //Приоритет: executionId > jobId > companyId
        if (executionId) {
            // Приоритет 1: executionId
            marketingBonusCalculatorDP.getStatusAll(
                executionId,
                handleAllCompaniesResponse,
                handleError
            );
            return;
        }

        if (jobId) {
            //Приоритет 2: jobId
            marketingBonusCalculatorDP.getStatusAllByJobId(
                jobId,
                handleAllCompaniesResponse,
                handleError
            );
            return;
        }

        if (selectedCompanyId) {
            //Приоритет 3: companyId
            marketingBonusCalculatorDP.getStatusOne(
                selectedCompanyId,
                handleOneCompanyResponse,
                handleError
            );
            return;
        }

        updateStatusMessage('Warning', companyName, undefined, 'Нет данных для проверки статуса');
        setIsCalculating(false);
        setIsLoading(false);

    }, [selectedCompanyId, executionId, jobId, getCompanyName, updateStatusMessage]);

    //СТАТУС ПОСЛЕДНЕГО ПЕРЕСЧЕТА
    const loadLastExecution = useCallback(() => {
        marketingBonusCalculatorDP.getStatusAllLast(
            (data: IMarketingBonusCalculationAllCompaniesStatusDTO) => {
                if (data) {
                    const status = data.status;
                    const completedAt = data.completedAt || undefined;
                    const errorMessage = data.errorMessage || undefined;
                    const companyName = getCompanyName();

                    if (status === 'Running') {
                        updateStatusMessage(status, companyName, completedAt, errorMessage, false);
                        if (data.executionId) {
                            setExecutionId(data.executionId);
                        }
                        if (data.jobId) {
                            setJobId(data.jobId);
                        }
                    } else if (status === 'Completed' || status === 'Failed') {
                        updateStatusMessage(status, companyName, completedAt, errorMessage, false);
                    }
                }
            },
            (err: IErrorResponseMarketingBonusCalculationCompanyStatus) => {
                console.warn(err);
            }
        );
    }, [getCompanyName, updateStatusMessage]);

    useEffect(() => {
        loadCompanies();
    }, [])

    return (
        <PluginWrapper>
            <GridWrapper cols={1}>
                <>
                    <div className={styles.viewWrapper}>
                        <BaseCommandsPanel groups={[
                            [
                                {
                                    iconButton: <Tooltip title={baseT('refresh')}>
                                        <RotationRightButton
                                            sizeVariant="mini"
                                            onClick={(!selectedCompanyId && !executionId && !jobId) ? loadLastExecution : handleRefreshStatus}
                                            disabled={isLoading}
                                        />
                                    </Tooltip>
                                },
                                {
                                    iconButton: <Tooltip title={baseT('recoverNote')}>
                                        <DeleteButton
                                            sizeVariant="mini"
                                            onClick={() => {
                                                setSelectedCompanyId(null);
                                                setStatusMessage(null);
                                                setExecutionId(null);
                                                setJobId(null)
                                            }}
                                            disabled={isLoading || isCalculating || !selectedCompanyId}
                                        />
                                    </Tooltip>
                                },
                            ],
                        ]}
                        />
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder={baseT("selectCompany")}
                            value={selectedCompanyId}
                            onChange={handleCompanyChange}
                            disabled={isLoading || isCalculating}
                            loading={isLoading}
                            options={companies}
                            optionFilterProp="label"
                            notFoundContent={baseT("noCompanies")}
                        />
                    </div>
                </>
                <Button
                    className={styles.button}
                    variant={"primary"}
                    onClick={handleCalculate}
                    disabled={isLoading}
                >
                    {isCalculating ? baseT('calculating') : baseT('calculate')}
                </Button>

                {statusMessage && (
                    <div className={`${styles.statusContainer} ${styles[statusMessage.type]}`}>
                        <span className={styles.statusText}>
                            {isCalculating ? <Spinner /> : statusMessage.text}
                        </span>
                    </div>
                )}
            </GridWrapper>
        </PluginWrapper>
    );
};